/**
 * Severe weather alerts — api.weather.gov (NWS). PURE normalization + a fetch
 * shell. No React imports.
 *
 * WHY NOT OpenWeatherMap: alerts ship only in One Call 3.0, which a free key
 * is explicitly refused from (verified 401). The spec's premise that the OWM
 * free tier can serve severe alerts is factually wrong. NWS is keyless, sends
 * `Access-Control-Allow-Origin: *`, and was probed returning live alerts.
 *
 * NWS asks API clients for a User-Agent. A browser cannot set one, and any
 * custom header would trigger a preflight this endpoint does not need. So the
 * request is a bare GET with no headers — verified working from a browser origin.
 *
 * US-ONLY. Outside the US the endpoint has nothing to say, and the correct
 * degraded state is simply never showing the indicator — never an error. So
 * every failure here resolves to an empty array, and the caller treats an
 * alerts failure as non-fatal.
 */

import type { WeatherAlert } from '../../config/contracts';
import { fetchJson } from '../http';

const BASE = 'https://api.weather.gov/alerts/active';

interface NwsProperties {
  readonly event?: string;
  readonly severity?: string;
  readonly headline?: string;
  readonly description?: string;
  readonly onset?: string;
  readonly effective?: string;
  readonly expires?: string;
  readonly ends?: string;
  readonly status?: string;
  readonly messageType?: string;
}

interface NwsFeature {
  readonly id?: string;
  readonly properties?: NwsProperties;
}

export interface NwsResponse {
  readonly features?: readonly NwsFeature[];
}

export function alertsUrl(lat: number, lon: number): string {
  // The `point` parameter wants a bare "lat,lon" — URLSearchParams would
  // percent-encode the comma, which NWS rejects.
  return `${BASE}?point=${lat.toFixed(4)},${lon.toFixed(4)}`;
}

export type AlertSeverity = WeatherAlert['severity'];

/** warning first: the top bar renders alerts[0] and must show the worst one. */
const SEVERITY_RANK: Readonly<Record<AlertSeverity, number>> = {
  warning: 0,
  watch: 1,
  advisory: 2,
};

/**
 * NWS carries severity in two places that disagree. `properties.severity`
 * (Extreme/Severe/Moderate/Minor/Unknown) describes the hazard; the event NAME
 * carries the actionable distinction the spec cares about — a "Tornado Watch"
 * and a "Tornado Warning" are both Severity=Extreme but mean very different
 * things. So the name wins, and CAP severity is only the fallback.
 *
 * `hasSevereAlert` (and therefore the pulsing top-bar indicator) fires on
 * 'warning' alone, per the contract.
 */
export function classifySeverity(event: string | undefined, capSeverity: string | undefined): AlertSeverity {
  const name = (event ?? '').toLowerCase();
  if (name.includes('warning')) return 'warning';
  if (name.includes('watch')) return 'watch';
  if (name.includes('advisory') || name.includes('statement')) return 'advisory';

  switch ((capSeverity ?? '').toLowerCase()) {
    case 'extreme':
    case 'severe':
      return 'warning';
    case 'moderate':
      return 'watch';
    default:
      return 'advisory';
  }
}

function epoch(...candidates: ReadonlyArray<string | undefined>): number | null {
  for (const c of candidates) {
    if (typeof c !== 'string' || c === '') continue;
    const t = new Date(c).getTime();
    if (!Number.isNaN(t)) return t;
  }
  return null;
}

/**
 * Pure. Drops test/exercise/draft messages and cancellations, drops anything
 * already expired, dedupes by id, and sorts worst-first.
 */
export function normalizeAlerts(raw: NwsResponse, now: number = Date.now()): WeatherAlert[] {
  const seen = new Set<string>();
  const out: WeatherAlert[] = [];

  for (const feature of raw.features ?? []) {
    const p = feature.properties;
    if (p === undefined) continue;

    // 'Actual' excludes Test / Exercise / System / Draft feeds.
    if (typeof p.status === 'string' && p.status !== 'Actual') continue;
    if (p.messageType === 'Cancel') continue;

    const event = typeof p.event === 'string' && p.event.trim() !== '' ? p.event.trim() : null;
    if (event === null) continue;

    const start = epoch(p.onset, p.effective) ?? now;
    const end = epoch(p.ends, p.expires) ?? start;
    if (end < now) continue; // already over

    const id = typeof feature.id === 'string' && feature.id !== '' ? feature.id : `${event}:${start}`;
    if (seen.has(id)) continue;
    seen.add(id);

    const description =
      typeof p.headline === 'string' && p.headline.trim() !== ''
        ? p.headline.trim()
        : typeof p.description === 'string' && p.description.trim() !== ''
          ? p.description.trim()
          : undefined;

    out.push({
      id,
      event,
      severity: classifySeverity(event, p.severity),
      start,
      end,
      ...(description === undefined ? {} : { description }),
    });
  }

  out.sort((a, b) => {
    const bySeverity = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    return bySeverity !== 0 ? bySeverity : a.start - b.start;
  });
  return out;
}

/**
 * Never throws and never reports failure: outside the US this endpoint 4xxs,
 * and that is a supported, silent state — not an error the panel should show.
 */
export async function fetchNwsAlerts(lat: number, lon: number): Promise<WeatherAlert[]> {
  const res = await fetchJson<NwsResponse>(alertsUrl(lat, lon), { timeoutMs: 8_000 });
  if (!res.ok) return [];
  return normalizeAlerts(res.data);
}
